import { Plugin, Command, FileDialogButtonView, FileRepository, PendingActions, ModelLiveRange, IconImportWord } from 'ckeditor5';
import { importDocx, DocxImportError } from '../../../../lib/docx';
import { toDocxError } from '../../../../lib/docx/errors';
import { IMPORT_TIMEOUT_MS } from '../../../../lib/docx/limits';
import { uploadAssets, substituteAssets } from './uploadAssets';
import { insertImportedHtml } from './insertImportedHtml';

/*
 * "Import from Word": converts a .docx in a Web Worker, uploads its images
 * through the editor's upload adapter and inserts the result at the cursor as
 * one undo step.
 *
 * Observables: state ('idle' | 'converting' | 'uploading' | 'inserting'), progress (0–1).
 * Events: 'done' ({warnings, stats}), 'error' (DocxImportError).
 * Config: importWord.maxImportBytes (default 2 MB) — the editor's content plus
 * the import may not exceed it.
 */

const READ_ONLY_LOCK = 'import-word';
const ACCEPT = '.docx,.docm,.dotx,.dotm,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_NAME = /\.(docx|docm|dotx|dotm)$/i;
const DEFAULT_MAX_IMPORT_BYTES = 2000000;

class ImportWordCommand extends Command {
    refresh() {
        const plugin = this.editor.plugins.get('ImportWord');
        this.isEnabled = plugin.state === 'idle' && this.editor.model.canEditAt(this.editor.model.document.selection);
    }

    /**
     * @param {{file: File}} options
     * @returns {Promise<{warnings: object[], stats: object}>}
     */
    execute({ file } = {}) {
        return this.editor.plugins.get('ImportWord').importFile(file);
    }
}

export default class ImportWord extends Plugin {
    static get pluginName() {
        return 'ImportWord';
    }

    static get requires() {
        return [FileRepository, PendingActions];
    }

    init() {
        const editor = this.editor;
        this.set('state', 'idle');
        this.set('progress', 0);
        this._abort = null;
        this._wasFocused = false;

        editor.config.define('importWord', { maxImportBytes: DEFAULT_MAX_IMPORT_BYTES });

        const command = new ImportWordCommand(editor);
        editor.commands.add('importWord', command);
        this.on('change:state', () => command.refresh());

        this.listenTo(editor.editing.view.document, 'change:isFocused', (evt, name, isFocused) => {
            if (isFocused) this._wasFocused = true;
        });

        editor.ui.componentFactory.add('importWord', (locale) => {
            const view = new FileDialogButtonView(locale);
            view.set({
                acceptedType: ACCEPT,
                allowMultipleFiles: false,
                label: locale.t('Import from Word'),
                icon: IconImportWord,
                tooltip: true,
            });
            view.bind('isEnabled').to(command);
            view.on('done', (evt, files) => {
                const file = files && files[0];
                if (!file) return;
                // Failures are reported through the 'error' event.
                Promise.resolve(editor.execute('importWord', { file })).catch(() => {});
            });
            return view;
        });
    }

    /** Cancels a running import. */
    cancel() {
        if (this._abort) this._abort.abort();
    }

    destroy() {
        this.cancel();
        super.destroy();
    }

    _setState(state, progress) {
        this.state = state;
        if (progress !== undefined) this.progress = progress;
    }

    /**
     * Runs one import. Use the `importWord` command rather than calling this directly.
     * @param {File} file
     */
    async importFile(file) {
        const editor = this.editor;
        if (this.state !== 'idle') throw new DocxImportError('INTERNAL', 'an import is already running');
        const model = editor.model;
        const abort = new AbortController();
        this._abort = abort;
        const timeout = setTimeout(() => abort.abort(), IMPORT_TIMEOUT_MS);
        const pendingActions = editor.plugins.get('PendingActions');
        const pending = pendingActions.add(editor.locale.t('Importing a Word document'));
        const liveRange = ModelLiveRange.fromRange(model.document.selection.getFirstRange());
        const wasFocused = this._wasFocused;
        let locked = false;
        const unlock = () => {
            if (locked && editor.state !== 'destroyed') {
                editor.disableReadOnlyMode(READ_ONLY_LOCK);
                locked = false;
            }
        };
        // The host replacing the whole document (e.g. switching to another
        // situation) makes the captured position meaningless.
        this.listenTo(editor.data, 'set', () => abort.abort(), { priority: 'highest' });
        const checkAborted = () => {
            if (abort.signal.aborted || editor.state === 'destroyed') throw new DocxImportError('ABORTED');
        };

        try {
            if (!file || (!DOCX_NAME.test(file.name || '') && !/wordprocessingml|ms-word/.test(file.type || ''))) {
                throw new DocxImportError('NOT_A_ZIP', 'not a .docx file');
            }
            editor.enableReadOnlyMode(READ_ONLY_LOCK);
            locked = true;
            this._setState('converting', 0);

            const phases = { unzip: 0.05, parse: 0.15, resolve: 0.25, emit: 0.35 };
            const result = await importDocx(file, {
                signal: abort.signal,
                onProgress: (phase) => this._setState('converting', phases[phase] || this.progress),
            });
            checkAborted();

            const maxBytes = editor.config.get('importWord.maxImportBytes') || DEFAULT_MAX_IMPORT_BYTES;
            if (result.html.length + editor.data.get().length > maxBytes) throw new DocxImportError('CONTENT_TOO_LARGE');

            const warnings = [...result.warnings];
            let html = result.html;
            if (result.assets.length) {
                this._setState('uploading', 0.4);
                const { urls, failed } = await uploadAssets(editor.plugins.get('FileRepository'), result.assets, {
                    signal: abort.signal,
                    onProgress: (done, total) => this._setState('uploading', 0.4 + 0.5 * (done / total)),
                });
                checkAborted();
                if (failed) warnings.push({ code: 'IMAGE_UPLOAD_FAILED', count: failed });
                html = substituteAssets(html, urls);
            }

            this._setState('inserting', 0.9);
            unlock();
            checkAborted();
            if (editor.isReadOnly) throw new DocxImportError('TARGET_READONLY');
            insertImportedHtml(editor, html, { range: liveRange.toRange(), wasFocused });

            const report = { warnings, stats: result.stats };
            this.fire('done', report);
            return report;
        } catch (err) {
            const error = toDocxError(err);
            this.fire('error', error);
            throw error;
        } finally {
            clearTimeout(timeout);
            unlock();
            this.stopListening(editor.data, 'set');
            liveRange.detach();
            pendingActions.remove(pending);
            this._abort = null;
            this._setState('idle', 0);
        }
    }
}
